import axiosInstance from '../../utils/axiosInstance';

export type DeviceKind = 'imlock' | 'imcontrol' | 'imsecure';
export type DeviceStatusFilter = 'IN_USE' | 'AVAILABLE' | 'ARCHIVED';
// iMLock-family, iMControl binary, and ioti / iMSecure normalised states
// share a single column server-side. The frontend tone/label tables key
// off these values; keep them in sync with device-data-api's mappers.
export type DeviceLockState =
  // iMLock
  | 'ALL_LOCKED'
  | 'LEFT_UNLOCKED'
  | 'CRADLE_OPEN'
  | 'CUT'
  | 'CUSTOM'
  // iMControl (binary)
  | 'LOCKED'
  | 'UNLOCKED'
  // ioti / iMSecure
  | 'ALERT'
  | 'ACTIVE'
  | 'IDLE'
  | 'INACTIVE';

export interface DeviceReportingPolicy {
  effective_state: 'INACTIVE' | 'IDLE' | 'ACTIVE' | 'ALERT' | string;
  state_label: string;
  expected_interval_seconds: number;
  expected_interval_label: string;
  display_label: string;
  immediate_on_transition: boolean;
  is_overdue: boolean | null;
  freshness: 'current' | 'overdue' | 'unknown' | string;
  last_report_age_seconds: number | null;
  connection_status: 'online' | 'offline' | 'unknown' | string;
  connection_reason: string;
  policy_source: string;
  firmware_version: string | null;
  modem_capable: boolean;
  signal_strength_dbm: number | null;
  signal_quality: number | null;
  signal_label: 'strong' | 'fair' | 'weak' | string | null;
  battery_voltage: number | null;
  ble_cutoff_voltage: number;
  modem_cutoff_voltage: number | null;
  battery_cutoff_voltage: number;
  battery_cutoff_context: string;
  battery_warning_status:
    | 'normal'
    | 'low'
    | 'critical'
    | 'critical_reserve'
    | 'unknown'
    | string;
}

export interface Device {
  id: string;
  serial_number: string;
  device_id: string;
  external_device_id?: string | null;
  device_type: DeviceKind;
  latitude: string;
  longitude: string;
  battery_percentage: number | null;
  battery_available?: boolean;
  vehicle_alias: string;
  vehicle_registration: string;
  driver_full_name: string;
  status: 'online' | 'offline' | 'unknown';
  connectivity_status?: 'online' | 'offline' | 'unknown' | string | null;
  connection_reason?: string | null;
  lock_state: DeviceLockState;
  reporting_frequency_seconds: number | null;
  reporting_policy?: DeviceReportingPolicy | null;
  last_seen_at?: string | number | null;
  last_seen?: string | number | null;
  last_location_at?: string | number | null;
  company_name: string;
  associated_rtl?: string;
  device_status: DeviceStatusFilter;
}

export interface ListDevicesRequest {
  company_id: string;
  device_type?: DeviceKind[];
  device_status?: DeviceStatusFilter;
  search_term?: string;
  page?: number;
  page_size?: number;
}

export interface ListDevicesResponse {
  statusCode: number;
  data: {
    data: Device[];
    pagination: {
      page: number;
      page_size: number;
      total_count: number;
      total_pages: number;
    };
  };
  message: string;
  error: string | null;
}

export const listDevices = async (
  body: ListDevicesRequest
): Promise<ListDevicesResponse> => {
  const response = await axiosInstance.post('/device/list', body);
  return response.data;
};

export interface GetDeviceRequest {
  company_id: string;
  device_id: string;
  include_history?: boolean;
}

export interface DeviceHistoryEntry {
  timestamp: string | null;
  lock_state: string | null;
  latitude: number | null;
  longitude: number | null;
  battery_percentage: number | null;
  temperature?: number | null;
  humidity?: number | null;
}

export interface GetDeviceResponse {
  statusCode: number;
  data: {
    device: Device;
    history?: DeviceHistoryEntry[];
  };
  message: string;
  error: string | null;
}

interface CanonicalDeviceDetailResponse {
  statusCode: number;
  data?: {
    device?: Device | { device?: Device; history?: DeviceHistoryEntry[] };
    history?: DeviceHistoryEntry[];
  };
  message: string;
  error: string | null;
}

type CanonicalDevicePayload = NonNullable<
  CanonicalDeviceDetailResponse['data']
>['device'];

const isWrappedDevicePayload = (
  value: CanonicalDevicePayload
): value is { device?: Device; history?: DeviceHistoryEntry[] } =>
  Boolean(value && typeof value === 'object' && 'device' in value);

const adaptCanonicalDeviceDetail = (
  response: CanonicalDeviceDetailResponse
): GetDeviceResponse => {
  const devicePayload = response.data?.device;
  const device = isWrappedDevicePayload(devicePayload)
    ? devicePayload.device
    : devicePayload;
  const history = isWrappedDevicePayload(devicePayload)
    ? devicePayload.history
    : response.data?.history;

  if (!device) {
    throw new Error('Canonical device response missing device');
  }

  return {
    statusCode: response.statusCode,
    data: {
      device,
      ...(history ? { history } : {}),
    },
    message: response.message,
    error: response.error,
  };
};

export const getDevice = async (
  body: GetDeviceRequest
): Promise<GetDeviceResponse> => {
  try {
    const response = await axiosInstance.get<CanonicalDeviceDetailResponse>(
      `/devices/${body.device_id}`,
      {
        params: {
          company_id: body.company_id,
          ...(body.include_history ? { include: 'timeline' } : {}),
        },
      }
    );
    return adaptCanonicalDeviceDetail(response.data);
  } catch (error) {
    console.warn(
      '[apiDevices] canonical /devices/{id} failed; falling back to /device/get',
      error
    );
    const response = await axiosInstance.post('/device/get', body);
    return response.data;
  }
};

export interface DeviceTelemetryReading {
  timestamp: string;
  gps?: { lat: number; lng: number } | null;
  battery_level?: number | null;
  temperature?: number | null;
  humidity?: number | null;
  light?: number | null;
  shock?: number | null;
  lock_state?: string | null;
  seal_state?: string | null;
}

// Pre-filtered, per-section series shipped by the device-data-api so the
// detail screen can hide UI sections that have no data with a simple
// `length > 0` check (no map when no GPS, etc.).
export interface DeviceGpsPoint {
  timestamp: string;
  lat: number;
  lng: number;
  description?: string | null;
}

export interface DeviceBatteryPoint {
  timestamp: string;
  battery_level: number;
}

export interface DeviceLockStatePoint {
  timestamp: string;
  lock_state: string;
  lat?: number | null;
  lng?: number | null;
  battery_level?: number | null;
}

export interface GetDeviceTelemetryRequest {
  company_id: string;
  device_id: string; // registry UUID
  // 'latest' returns only the most recent reading regardless of age — the
  // right default for a "current state" screen on a device that may have
  // gone quiet for >24h.
  period?: 'latest' | '1h' | '6h' | '24h' | '7d';
  limit?: number;
  // Date-range mode (Activity Report): when start/end (ISO-8601) are set they
  // override `period` and the backend returns a ranged series, downsampled to
  // `max_points` per series (0 = no downsampling) for chart rendering.
  start?: string;
  end?: string;
  max_points?: number;
}

export interface DeviceTelemetryRequestOptions {
  signal?: AbortSignal;
}

export interface GetDeviceTelemetryResponse {
  statusCode: number;
  data: {
    device_id: string;
    type: string;
    period: string;
    readings: DeviceTelemetryReading[];
    // New per-section series. Empty array → hide the corresponding UI.
    gps_track?: DeviceGpsPoint[];
    battery_history?: DeviceBatteryPoint[];
    lock_state_history?: DeviceLockStatePoint[];
    // Set by the orchestrator when a windowed query (e.g. period=24h)
    // returned no rows so it fell back to the absolute-latest reading.
    // Frontend can use this to render a "last known reading from <X>" hint.
    fell_back_from_period?: string;
  };
  message: string;
  error: string | null;
}

interface CanonicalDeviceTimelineResponse {
  statusCode: number;
  data?: {
    telemetry?: GetDeviceTelemetryResponse['data'];
    events?: DeviceEvent[];
  };
  message: string;
  error: string | null;
}

const timelineParams = (
  body: GetDeviceTelemetryRequest | GetDeviceEventsRequest,
  include: 'telemetry' | 'events'
): Record<string, string | number> => {
  const params: Record<string, string | number> = {
    company_id: body.company_id,
    include,
  };

  if ('period' in body && body.period) params.period = body.period;
  if ('limit' in body && body.limit !== undefined) params.limit = body.limit;
  if ('start' in body && body.start) params.start = body.start;
  if ('end' in body && body.end) params.end = body.end;
  if ('max_points' in body && body.max_points !== undefined) {
    params.max_points = body.max_points;
  }

  return params;
};

export const getDeviceTelemetry = async (
  body: GetDeviceTelemetryRequest,
  options: DeviceTelemetryRequestOptions = {}
): Promise<GetDeviceTelemetryResponse> => {
  const { device_id, ...rest } = body;
  try {
    const response = await axiosInstance.get<CanonicalDeviceTimelineResponse>(
      `/devices/${device_id}/timeline`,
      {
        params: timelineParams(body, 'telemetry'),
        ...(options.signal ? { signal: options.signal } : {}),
      }
    );
    const telemetry = response.data.data?.telemetry;
    if (!telemetry) {
      throw new Error('Canonical device timeline missing telemetry');
    }
    return {
      statusCode: response.data.statusCode,
      data: telemetry,
      message: response.data.message,
      error: response.data.error,
    };
  } catch (error) {
    if (options.signal?.aborted) throw error;
    console.warn(
      '[apiDevices] canonical /devices/{id}/timeline telemetry failed; falling back to /device/{id}/telemetry',
      error
    );
    const response = options.signal
      ? await axiosInstance.post(`/device/${device_id}/telemetry`, rest, {
          signal: options.signal,
        })
      : await axiosInstance.post(`/device/${device_id}/telemetry`, rest);
    return response.data;
  }
};

export interface DeviceEvent {
  timestamp: string | null;
  event_type: string;
  severity?: string | null;
  lock_state?: string | null;
  actor?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  battery_voltage?: number | null;
}

export interface GetDeviceEventsRequest {
  company_id: string;
  device_id: string;
  limit?: number;
}

export interface GetDeviceEventsResponse {
  statusCode: number;
  data: { events: DeviceEvent[] };
  message: string;
  error: string | null;
}

export const getDeviceEvents = async (
  body: GetDeviceEventsRequest
): Promise<GetDeviceEventsResponse> => {
  const { device_id, ...rest } = body;
  try {
    const response = await axiosInstance.get<CanonicalDeviceTimelineResponse>(
      `/devices/${device_id}/timeline`,
      { params: timelineParams(body, 'events') }
    );
    return {
      statusCode: response.data.statusCode,
      data: { events: response.data.data?.events ?? [] },
      message: response.data.message,
      error: response.data.error,
    };
  } catch (error) {
    console.warn(
      '[apiDevices] canonical /devices/{id}/timeline events failed; falling back to /device/{id}/events',
      error
    );
    const response = await axiosInstance.post(
      `/device/${device_id}/events`,
      rest
    );
    return response.data;
  }
};

// --- Warnings / alert history (company-wide, aggregated from ioti_events) ---

export interface DeviceWarning {
  id: string;
  device_id: string;
  external_device_id: string | null;
  device_serial: string | null;
  device_type: DeviceKind | string | null;
  event_type: string;
  severity: string | null; // INFO | WARNING | ALERT
  status: string | null; // active | cleared
  timestamp: string | null;
  details?: Record<string, unknown> | null;
  latitude?: number | string | null;
  longitude?: number | string | null;
}

export interface GetDeviceWarningsHistoryRequest {
  company_id: string;
  device_type?: DeviceKind[]; // defaults to ['imsecure'] server-side
  search_term?: string;
  severity?: string;
  status?: 'active' | 'cleared';
  alert_types?: string[]; // event_type filter (case-insensitive)
  start?: string; // ISO-8601
  end?: string; // ISO-8601
  page?: number;
  page_size?: number;
}

export interface GetDeviceWarningsHistoryResponse {
  statusCode: number;
  data: {
    warnings: DeviceWarning[];
    total: number;
    total_items: number;
    total_pages: number;
    page_number: number;
  };
  message: string;
  error: string | null;
}

export const getDeviceWarningsHistory = async (
  body: GetDeviceWarningsHistoryRequest
): Promise<GetDeviceWarningsHistoryResponse> => {
  const response = await axiosInstance.post('/device/warnings-history', body);
  return response.data;
};

/**
 * Convenience wrapper: returns AVAILABLE-status devices for a company.
 * Optional `types` narrows to a subset (e.g. ['imlock']).
 */
export const listAvailableDevices = async (
  companyId: string,
  types?: DeviceKind[]
): Promise<ListDevicesResponse> => {
  return listDevices({
    company_id: companyId,
    device_status: 'AVAILABLE',
    ...(types && types.length ? { device_type: types } : {}),
    page: 1,
    page_size: 200,
  });
};

// ---------- Customer self-serve claim ----------

export interface ClaimDeviceInput {
  serial_number: string;
  // Lowercase family the orchestrator expects (imlock | imcontrol | imsecure);
  // it upper-cases before forwarding to the backend.
  device_type: DeviceKind;
  // device-data-api Device row UUID — optional join key for the telemetry
  // overlay. Omitted here (customers claim by serial only).
  external_device_id?: string;
}

export interface ClaimDevicesRequest {
  company_id: string;
  devices: ClaimDeviceInput[];
}

// The orchestrator returns full DeviceSummary rows for each claimed device;
// callers only need the new registry `id` (to attach it) and the serial.
export interface ClaimedDevice {
  id: string;
  serial_number: string;
  device_type?: string;
}

export interface ClaimDeviceFailure {
  serial_number: string;
  reason: string;
}

export interface ClaimDevicesResponse {
  statusCode: number;
  data: {
    claimed: ClaimedDevice[];
    failed: ClaimDeviceFailure[];
  };
  message: string;
  error: string | null;
}

/**
 * Customer self-serve device claim — registers one or more devices (by serial)
 * to the caller's own company. Mirrors `POST /api/v1/device/claim` (JWT).
 *
 * Per-serial problems (unknown serial, already claimed, …) come back in
 * `data.failed` inside a 200 envelope, so callers MUST inspect both `claimed`
 * and `failed` rather than trusting the HTTP status alone.
 */
export const claimDevices = async (
  body: ClaimDevicesRequest
): Promise<ClaimDevicesResponse> => {
  const response = await axiosInstance.post('/device/claim', body);
  return response.data;
};

// ---------- Admin (SuperUser) endpoints ----------

export type AdminAssignmentFilter = 'ASSIGNED' | 'NOT_ASSIGNED';

export interface AdminListDevicesRequest {
  admin_email: string;
  search_term?: string;
  device_type?: DeviceKind[];
  assigned_to_client?: AdminAssignmentFilter[];
  page?: number;
  page_size?: number;
}

// Same wire shape as ListDevicesResponse — DeviceSummary rows + pagination.
export const adminListDevices = async (
  body: AdminListDevicesRequest
): Promise<ListDevicesResponse> => {
  const response = await axiosInstance.post('/device/admin-list', body);
  return response.data;
};

export interface AdminAssignDevicesRequest {
  admin_email: string;
  company_id: string;
  device_ids: string[];
}

export interface AdminAssignDevicesResponse {
  statusCode: number;
  data: {
    assigned: { id: string; company_name: string }[];
    skipped: { id: string; reason: string }[];
  };
  message: string;
  error: string | null;
}

export const adminAssignDevices = async (
  body: AdminAssignDevicesRequest
): Promise<AdminAssignDevicesResponse> => {
  const response = await axiosInstance.post('/admin/assign-devices', body);
  return response.data;
};

export interface AdminUploadDevicesCsvResponse {
  statusCode: number;
  data: {
    created: string[];
    failed: { serial_number: string; reason: string }[];
  };
  message: string;
  error: string | null;
}

export const adminUploadDevicesCsv = async (
  adminEmail: string,
  file: File
): Promise<AdminUploadDevicesCsvResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('admin_email', adminEmail);
  const response = await axiosInstance.post(
    '/device/admin-upload-csv',
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    }
  );
  return response.data;
};

export interface AdminFleetTypeSummary {
  device_type: DeviceKind;
  total: number;
  assigned: number;
  unassigned: number;
  in_use: number;
  archived: number;
  stale: number;
  low_battery: number;
}

export interface AdminDeviceSummaryResponse {
  statusCode: number;
  data: {
    fleets: AdminFleetTypeSummary[] | null;
    rtl: { total: number; assigned: number; unassigned: number } | null;
  };
  message: string;
  error: string | null;
}

export const fetchAdminDeviceSummary = async (
  adminEmail: string
): Promise<AdminDeviceSummaryResponse> => {
  const response = await axiosInstance.post('/device/admin-summary', {
    admin_email: adminEmail,
  });
  return response.data;
};

// ---------- Admin: unassigned registry devices (device_data reconciliation) ----------

export interface UnassignedRegistryDevice {
  registry_id: string;
  serial_number: string;
  device_type: 'IMLOCK' | 'IMCONTROL' | 'IMSECURE';
  product_family: 'imlock' | 'imcontrol' | 'imsecure';
  name: string | null;
  fw_ver: string | null;
  first_seen: string | null;
  last_updated: string | null;
}

export interface AdminUnassignedCounts {
  unassigned: number;
  registered: number;
  excluded_imsense: number;
  excluded_unclassified: number;
}

export interface AdminUnassignedDevicesResponse {
  statusCode: number;
  data: {
    devices: UnassignedRegistryDevice[];
    counts: AdminUnassignedCounts;
  };
  message: string;
  error: string | null;
}

export const adminListUnassignedDevices = async (
  adminEmail: string
): Promise<AdminUnassignedDevicesResponse> => {
  const response = await axiosInstance.post('/device/admin-unassigned', {
    admin_email: adminEmail,
  });
  return response.data;
};

export interface AdminRegisterAssignRequest {
  admin_email: string;
  company_id: string;
  devices: {
    serial_number: string;
    device_type: 'IMLOCK' | 'IMCONTROL' | 'IMSECURE';
    external_device_id: string;
  }[];
}

export interface AdminRegisterAssignResponse {
  statusCode: number;
  data: {
    registered: string[];
    skipped: { serial_number: string; reason: string }[];
  };
  message: string;
  error: string | null;
}

export const adminRegisterAssignDevices = async (
  body: AdminRegisterAssignRequest
): Promise<AdminRegisterAssignResponse> => {
  const response = await axiosInstance.post(
    '/device/admin-register-assign',
    body
  );
  return response.data;
};

export interface RemoveDeviceResponse {
  statusCode: number;
  data: null;
  message: string;
  error: string | null;
}

export const removeDevice = async (
  deviceId: string
): Promise<RemoveDeviceResponse> => {
  const response = await axiosInstance.post('/device/remove', {
    device_id: deviceId,
  });
  return response.data;
};
